import React, { useEffect, useState } from 'react'
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, makeStyles, TextField } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { addMarket, MarketModel, updateMarket } from '../../redux/actions/dashboard'

// url je povinna (DB NOT NULL) a musi byt http(s) (rovnako ako backend MarketService)
const isValidUrl = (url: string) => /^https?:\/\/\S+$/i.test(url.trim())

const useStyles = makeStyles((theme) => ({
    field: {
        marginTop: theme.spacing(1),
        width: 480,
        display: 'block',
    },
}))

interface Props {
    open: boolean
    // undefined = novy market
    market?: MarketModel
    onClose: () => void
    onSaved: () => void
    onError: (msg: string) => void
}

export default function MarketDialog({ open, market, onClose, onSaved, onError }: Props) {
    const classes = useStyles()
    const { t } = useTranslation()
    const [name, setName] = useState('')
    const [url, setUrl] = useState('')
    const [description, setDescription] = useState('')
    const [saving, setSaving] = useState(false)
    const [touched, setTouched] = useState(false)

    useEffect(() => {
        if (!open) return
        setName(market?.name ?? '')
        setUrl(market?.url ?? '')
        setDescription(market?.description ?? '')
        setTouched(false)
    }, [open, market])

    const nameError = touched && !name.trim()
    const urlError = touched && !isValidUrl(url)

    const close = () => {
        if (!saving) onClose()
    }

    const handleSave = async () => {
        setTouched(true)
        if (!name.trim() || !isValidUrl(url)) return
        const body: MarketModel = {
            name: name.trim(),
            url: url.trim(),
            description: description.trim() || null,
        }
        setSaving(true)
        try {
            if (market?.id) {
                await updateMarket(market.id, body)
            } else {
                await addMarket(body)
            }
            onSaved()
        } catch {
            onError(t('dashboard.errorSavingMarket'))
        } finally {
            setSaving(false)
        }
    }

    return (
        <Dialog open={open} onClose={close}>
            <DialogTitle>{market ? t('dashboard.editMarket') : t('dashboard.addMarket')}</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    fullWidth
                    required
                    className={classes.field}
                    variant="filled"
                    label={t('common.name')}
                    value={name}
                    inputProps={{ maxLength: 64 }}
                    error={nameError}
                    helperText={nameError ? t('common.required') : ' '}
                    onChange={(e) => setName(e.target.value)}
                />
                <TextField
                    fullWidth
                    required
                    className={classes.field}
                    variant="filled"
                    label={t('dashboard.marketUrl')}
                    placeholder="https://"
                    value={url}
                    inputProps={{ maxLength: 256 }}
                    error={urlError}
                    helperText={urlError ? t('dashboard.invalidUrl') : ' '}
                    onChange={(e) => setUrl(e.target.value)}
                />
                <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    className={classes.field}
                    variant="filled"
                    label={t('common.description')}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={close} disabled={saving}>
                    {t('common.cancel')}
                </Button>
                <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
                    {saving ? <CircularProgress size={20} /> : t('common.save')}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
